import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  IconButton,
  Chip,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  AccessTime,
  People,
  TrendingUp,
  Add,
  Visibility,
  Download,
  CheckCircle,
  Cancel,
  Warning,
  Schedule,
  FilterList,
  Search,
  Refresh,
  ExitToApp,
  Close,
} from '@mui/icons-material';
import userService from '../../services/userService';
import storeService from '../../services/storeService';
import attendanceService, { AttendanceSummary } from '../../services/attendanceService';
import clusterService from '../../services/clusterService';
import shiftService, { Shift } from '../../services/shiftService';
import { User, Store } from '../../types';
import AttendanceDetailModal from '../../components/attendance/AttendanceDetailModal';
import ProfileAvatar from '../../components/common/ProfileAvatar';
import ImageDialog from '../../components/common/ImageDialog';
import { useAuth } from '../../hooks/useAuth';
import AttendanceExportPage from './AttendanceExportPage';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`attendance-tabpanel-${index}`}
      aria-labelledby={`attendance-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AttendancePage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [clusters, setClusters] = useState<any[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [attendanceData, setAttendanceData] = useState<any[]>([]);
  const [filteredAttendanceData, setFilteredAttendanceData] = useState<any[]>([]);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    role: 'all',
    store_id: 'all',
    cluster_id: 'all',
    shift_id: 'all',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    is_late: 'all',
    min_hours: '',
    max_hours: '',
  });
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    presentToday: 0,
    absentToday: 0,
    lateToday: 0,
    weekOffToday: 0,
    checkedOutToday: 0,
    avgHoursToday: 0,
  });
  const [initialTotalEmployees, setInitialTotalEmployees] = useState(0);
  // Counts across ALL matching employees, computed by the backend. Used for the
  // cards whenever no browser-only filter (search, shift, late, hours) narrows the rows.
  const [serverSummary, setServerSummary] = useState<AttendanceSummary | null>(null);
  const [selectedAttendance, setSelectedAttendance] = useState<any>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [selectedImageAlt, setSelectedImageAlt] = useState<string>('');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    store_id: 'all',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    // 'inactive' exports deactivated employees (leavers / notice period) who
    // punched in during the range, so they can still be paid
    employee_status: 'active' as 'active' | 'inactive',
  });

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'success' | 'error' | 'warning' | 'info',
  });

  // Helper functions for snackbar
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'error') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const fetchUsers = useCallback(async () => {
    try {
      const response = await userService.getUsers();
      const users = response.users || [];
      setUsers(users);
      setStats(prev => ({ ...prev, totalEmployees: users.length }));
    } catch (err: any) {
      showSnackbar('Failed to fetch users', 'error');
    }
  }, []);

  const fetchStores = useCallback(async () => {
    try {
      const response = await storeService.getStores({ limit: 1000 });
      setStores(response.stores || []);
    } catch (err: any) {
      showSnackbar('Failed to fetch stores', 'error');
    }
  }, []);

  const fetchClusters = useCallback(async () => {
    try {
      const response = await clusterService.getClusters({ limit: 1000 });
      setClusters(response.clusters || []);
    } catch (err: any) {
      showSnackbar('Failed to fetch clusters', 'error');
    }
  }, []);

  const fetchShifts = useCallback(async () => {
    try {
      const response = await shiftService.getShifts({ limit: 1000, is_active: true });
      setShifts(response.shifts || []);
    } catch {
      // non-critical, silently fail
    }
  }, []);

  const fetchAttendanceData = useCallback(async (override?: Partial<typeof filters>) => {
    try {
      const f = { ...filters, ...(override || {}) };
      // Handle "present" status - don't send to backend, filter client-side instead
      // Backend expects "in", "out", "absent", "late", "week_off", etc.
      const statusForBackend = f.status !== 'all' && f.status !== 'present' ? f.status : undefined;
      
      const response = await attendanceService.getAttendanceRecords({
        startDate: f.start_date || undefined,
        endDate: f.end_date || undefined,
        storeId: f.store_id !== 'all' ? f.store_id : undefined,
        clusterId: f.cluster_id !== 'all' ? f.cluster_id : undefined,
        status: statusForBackend,
        role: f.role !== 'all' ? f.role : undefined,
        // Backend loads the list in bulk now, so fetch everyone (API max is 1000).
        // At 200 the cards only counted the first 200 employees alphabetically.
        limit: 1000,
      });
      
      // Store raw data first, we'll transform it in useEffect
      const records = response.records || [];
      setAttendanceData(records);
      // Initialize filtered data - the useEffect will update it when filters change
      setFilteredAttendanceData(records);
      
      // Calculate initial stats from all records (before client-side filtering)
      // These will be updated by the filter useEffect to match filtered data
      const presentCount = records.filter((a: any) => a.status === 'in' || a.status === 'out').length;
      const lateCount = records.filter((a: any) => a.is_late === true).length;
      const absentCount = records.filter((a: any) => a.status === 'absent').length;
      const weekOffCount = records.filter((a: any) => a.status === 'week_off').length;
      const totalEmployees = response.pagination?.total ?? users.length;
      // Calculate total hours and average ONLY from present employees (status 'in' or 'out')
      const presentRecords = records.filter((a: any) => (a.status === 'in' || a.status === 'out') && parseFloat(a.total_hours) > 0);
      const totalHours = presentRecords.reduce((sum: number, a: any) => sum + (parseFloat(a.total_hours) || 0), 0);
      const avgHours = presentRecords.length > 0 ? totalHours / presentRecords.length : 0;
      
      const checkedOutCount = records.filter((a: any) => a.status === 'out').length;
      
      setInitialTotalEmployees(totalEmployees);
      const summary = response.summary || null;
      setServerSummary(summary);
      setStats(prevStats => (summary ? {
        ...prevStats,
        totalEmployees: summary.total_employees,
        presentToday: summary.present,
        absentToday: summary.absent,
        lateToday: summary.late,
        weekOffToday: summary.week_off,
        checkedOutToday: summary.checked_out,
        avgHoursToday: summary.avg_hours,
      } : {
        ...prevStats,
        totalEmployees: totalEmployees,
        presentToday: presentCount,
        absentToday: absentCount,
        lateToday: lateCount,
        weekOffToday: weekOffCount,
        checkedOutToday: checkedOutCount,
        avgHoursToday: Math.round(avgHours * 10) / 10, // Round to 1 decimal
      }));
    } catch (err: any) {
      // Fallback to mock data if API fails
      console.warn('API failed, using mock data:', err);
      // We'll handle mock data in a separate useEffect when users are loaded
    }
  }, [users, filters]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      // Stagger API calls to prevent rate limiting
      await fetchUsers();
      await new Promise(resolve => setTimeout(resolve, 200));
      await fetchStores();
      await new Promise(resolve => setTimeout(resolve, 200));
      await fetchClusters();
      fetchShifts();
      setLoading(false);
    };
    loadData();
  }, [fetchUsers, fetchStores, fetchClusters, fetchShifts]);

  useEffect(() => {
    if (users.length > 0 && stores.length > 0) {
      fetchAttendanceData();
    }
  }, [users.length, stores.length]);

  // Remove mock data fallback - we want real API data only
  // useEffect(() => {
  //   if (users.length > 0 && attendanceData && attendanceData.length === 0) {
  //     // Mock data logic removed - use real API data only
  //   }
  // }, [users, attendanceData?.length]);

  // Apply client-side filters for search/min/max hours/late only
  useEffect(() => {
    const localFiltered = attendanceData.filter((attendance) => {
      // Search
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const matches = (
          attendance.user?.first_name?.toLowerCase().includes(searchTerm) ||
          attendance.user?.last_name?.toLowerCase().includes(searchTerm) ||
          attendance.user?.employee_id?.toLowerCase().includes(searchTerm)
        );
        if (!matches) return false;
      }
      
      // Status filter
      // Note: Backend already filters by status for 'late', 'week_off', 'absent', etc.
      // So we only need to filter client-side for 'present' (which combines 'in' and 'out')
      // For other statuses, backend has already filtered, so we just verify the data matches
      if (filters.status !== 'all') {
        if (filters.status === 'present') {
          // "Present" includes both "in" and "out" status
          if (attendance.status !== 'in' && attendance.status !== 'out') return false;
        } else if (filters.status === 'late') {
          // Backend already filtered by late, but verify the record is actually late
          // Late employees must be present (status 'in' or 'out') AND have is_late = true
          if (attendance.status !== 'in' && attendance.status !== 'out') return false;
          if (!attendance.is_late) return false;
        } else if (filters.status === 'week_off') {
          // Backend already filtered by week_off, but verify the record is actually week_off
          if (attendance.status !== 'week_off') return false;
        } else {
          // For other statuses (absent, in, out), backend has already filtered
          // Just verify the status matches (should always match if backend filtered correctly)
          if (attendance.status !== filters.status) return false;
        }
      }
      
      // Role filter
      if (filters.role !== 'all') {
        if (attendance.user?.role !== filters.role) return false;
      }
      
      // Store filter
      if (filters.store_id !== 'all') {
        if (attendance.store?.id !== filters.store_id) return false;
      }
      
      // Cluster filter
      if (filters.cluster_id !== 'all') {
        if (attendance.user?.cluster?.id !== filters.cluster_id) return false;
    }

    // Shift filter
    if (filters.shift_id !== 'all') {
      const attendanceShiftId = attendance.shift?.id || attendance.user?.shift?.id;
      if (attendanceShiftId !== filters.shift_id) return false;
    }

    // Late filter
    if (filters.is_late !== 'all') {
      const isLate = filters.is_late === 'true';
        if (attendance.is_late !== isLate) return false;
      }
      
      // Hours
      if (filters.min_hours && !(attendance.total_hours >= parseFloat(filters.min_hours))) return false;
      if (filters.max_hours && !(attendance.total_hours <= parseFloat(filters.max_hours))) return false;
      return true;
    });
    setFilteredAttendanceData(localFiltered);

    // No browser-only filter active: the backend summary covers every matching
    // employee, so use it rather than counting the loaded rows.
    const hasClientOnlyFilter = !!filters.search || filters.shift_id !== 'all' ||
      filters.is_late !== 'all' || !!filters.min_hours || !!filters.max_hours;
    if (serverSummary && !hasClientOnlyFilter) {
      setStats(prevStats => ({
        ...prevStats,
        totalEmployees: serverSummary.total_employees,
        presentToday: serverSummary.present,
        absentToday: serverSummary.absent,
        lateToday: serverSummary.late,
        weekOffToday: serverSummary.week_off,
        checkedOutToday: serverSummary.checked_out,
        avgHoursToday: serverSummary.avg_hours,
      }));
      return;
    }
    
    // Update stats based on filtered data for consistency
    // Count each status from the filtered records
    // Present: includes both 'in' and 'out' statuses
    const presentCount = localFiltered.filter((a: any) => a.status === 'in' || a.status === 'out').length;
    // Late: records with is_late = true (but still present, so they have 'in' or 'out' status)
    const lateCount = localFiltered.filter((a: any) => a.is_late === true).length;
    // Absent: ONLY records with status='absent' (not punched in, excluding late, week_off, present)
    const absentCount = localFiltered.filter((a: any) => a.status === 'absent').length;
    // Week Off: records with status='week_off'
    const weekOffCount = localFiltered.filter((a: any) => a.status === 'week_off').length;
    // Checked Out: records with status='out'
    const checkedOutCount = localFiltered.filter((a: any) => a.status === 'out').length;
    
    // Calculate total hours and average ONLY from present employees (status 'in' or 'out' with hours > 0)
    // Exclude absent, week_off, and other non-present statuses from average calculation
    const presentRecords = localFiltered.filter((a: any) => (a.status === 'in' || a.status === 'out') && parseFloat(a.total_hours) > 0);
    const totalHours = presentRecords.reduce((sum: number, a: any) => sum + (parseFloat(a.total_hours) || 0), 0);
    const avgHours = presentRecords.length > 0 ? totalHours / presentRecords.length : 0;
    
    // Total employees should always show the actual total from API, not filtered count
    // This gives context about the total workforce vs filtered results
    const totalEmployeesCount = initialTotalEmployees || localFiltered.length;
    
    setStats(prevStats => ({
      ...prevStats,
      totalEmployees: totalEmployeesCount,
      presentToday: presentCount,
      absentToday: absentCount,
      lateToday: lateCount,
      weekOffToday: weekOffCount,
      checkedOutToday: checkedOutCount,
      avgHoursToday: Math.round(avgHours * 10) / 10,
    }));
  }, [filters.search, filters.status, filters.store_id, filters.cluster_id, filters.shift_id, filters.is_late, filters.min_hours, filters.max_hours, attendanceData, serverSummary]);

  // Refetch from backend when server-side filters change
  useEffect(() => {
    if (users.length === 0 || stores.length === 0) return;
    fetchAttendanceData();
  }, [filters.start_date, filters.end_date, filters.store_id, filters.cluster_id, filters.status, filters.role]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleExportClick = (employeeStatus: 'active' | 'inactive' = 'active') => {
    // Inactive report defaults to the last 30 days; active uses the current date range
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    setExportFilters({
      store_id: filters.store_id,
      start_date: employeeStatus === 'inactive' ? thirtyDaysAgo : filters.start_date,
      end_date: employeeStatus === 'inactive' ? new Date().toISOString().split('T')[0] : filters.end_date,
      employee_status: employeeStatus,
    });
    setExportDialogOpen(true);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      
      const params: any = {
        format: 'csv',
        startDate: exportFilters.start_date,
        endDate: exportFilters.end_date,
        employeeStatus: exportFilters.employee_status,
      };

      if (exportFilters.store_id !== 'all') {
        params.storeId = exportFilters.store_id;
      }

      const blob = await attendanceService.exportAttendanceData(params);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const prefix = exportFilters.employee_status === 'inactive' ? 'inactive_attendance_export' : 'attendance_export';
      link.download = `${prefix}_${exportFilters.start_date}_to_${exportFilters.end_date}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSnackbar('Attendance data exported successfully', 'success');
      setExportDialogOpen(false);
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'Failed to export attendance data', 'error');
    } finally {
      setExporting(false);
    }
  };



  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      role: 'all',
      store_id: 'all',
      cluster_id: 'all',
      shift_id: 'all',
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date().toISOString().split('T')[0],
      is_late: 'all',
      min_hours: '',
      max_hours: '',
    });
  };

  const handleUserClick = (userId: string) => {
    // Only navigate if userId is a valid UUID (not mock data)
    if (userId && userId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      window.location.href = `/users/${userId}/attendance`;
    } else {
      console.warn('Invalid user ID, cannot navigate:', userId);
    }
  };

  const getStatusColor = (status: string, isLate?: boolean, isHalfDay?: boolean) => {
    if (isHalfDay) return 'warning';
    // Show warning (orange) if late, regardless of status
    if (isLate) {
      return 'warning';
    }
    switch (status) {
      case 'in':
      case 'present': return 'success';
      case 'absent': return 'error';
      case 'late': return 'warning';
      case 'out': return 'info';
      case 'week_off': return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string, isLate?: boolean, lateMinutes?: number, isHalfDay?: boolean) => {
    if (isHalfDay) return 'Half Day';
    // Show "Late" if is_late is true, regardless of status
    if (isLate) {
      return lateMinutes ? `${lateMinutes}m Late` : 'Late';
    }
    switch (status) {
      case 'in': return 'Present';
      case 'out': return 'Out';
      case 'absent': return 'Absent';
      case 'late': return 'Late';
      case 'no_punch': return 'No Punch';
      case 'week_off': return 'Week Off';
      default: return (status || '').charAt(0).toUpperCase() + (status || '').slice(1);
    }
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return '-';
    const date = new Date(timeString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  const formatTotalHours = (attendance: any) => {
    // If total_hours is available, use it
    if (attendance.total_hours != null && attendance.total_hours !== undefined) {
      const totalHours = typeof attendance.total_hours === 'string' 
        ? parseFloat(attendance.total_hours) 
        : attendance.total_hours;
      
      if (isNaN(totalHours) || totalHours <= 0) return '-';
      
      const hours = Math.floor(totalHours);
      const minutes = Math.round((totalHours - hours) * 60);
      
      if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}m`;
      } else if (hours > 0) {
        return `${hours}h`;
      } else if (minutes > 0) {
        return `${minutes}m`;
      }
      return '-';
    }
    
    // If total_hours is not available, calculate from clock_in_time and clock_out_time
    if (attendance.clock_in_time && attendance.clock_out_time) {
      const clockIn = new Date(attendance.clock_in_time);
      const clockOut = new Date(attendance.clock_out_time);
      const diffMs = clockOut.getTime() - clockIn.getTime();
      const diffHours = diffMs / (1000 * 60 * 60);
      
      if (diffHours <= 0) return '-';
      
      const hours = Math.floor(diffHours);
      const minutes = Math.round((diffHours - hours) * 60);
      
      if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}m`;
      } else if (hours > 0) {
        return `${hours}h`;
      } else if (minutes > 0) {
        return `${minutes}m`;
      }
      return '-';
    }
    
    return '-';
  };

  const formatBreaks = (attendance: any) => {
    if (!attendance.breaks || !Array.isArray(attendance.breaks) || attendance.breaks.length === 0) {
      return '-';
    }

    const breaks = attendance.breaks;
    const totalMinutes = breaks.reduce((sum: number, b: any) => {
      return sum + (b.duration_minutes || 0);
    }, 0);

    if (totalMinutes === 0) {
      return '-';
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    let displayText = '';
    if (hours > 0) {
      displayText = `${hours}h ${minutes}m`;
    } else {
      displayText = `${minutes}m`;
    }

    // Show extra minutes if any
    const totalExtraMinutes = breaks.reduce((sum: number, b: any) => {
      return sum + (b.extra_minutes || 0);
    }, 0);

    if (totalExtraMinutes > 0) {
      displayText += ` (+${totalExtraMinutes}m)`;
    }

    // Show break date(s) - if multiple breaks, show date range or first date
    const breakDates = breaks
      .map((b: any) => b.date)
      .filter((date: string) => date)
      .filter((v: string, i: number, a: string[]) => a.indexOf(v) === i); // unique dates
    
    if (breakDates.length > 0) {
      const formattedDate = new Date(breakDates[0]).toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: 'short' 
      });
      displayText += ` (${formattedDate})`;
    }

    return displayText;
  };

  const getStatusIcon = (status: string, isLate?: boolean, isHalfDay?: boolean) => {
    if (isHalfDay) return <Schedule />;
    // Show warning icon if late, regardless of status
    if (isLate) {
      return <Warning />;
    }
    switch (status) {
      case 'in':
      case 'present': return <CheckCircle />;
      case 'absent': return <Cancel />;
      case 'late': return <Warning />;
      case 'out': return <Schedule />;
      default: return <Schedule />;
    }
  };

  // For MIS users, show only export page (after all hooks are declared)
  if (currentUser?.role === 'mis') {
    return <AttendanceExportPage />;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Top Action Bar */}
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
        {/* Search Field */}
        <TextField
          size="small"
          placeholder="Search employees..."
          value={filters.search}
          onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: '#6c757d', fontSize: 18 }} />,
          }}
          sx={{ 
            minWidth: 300,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#f8f9fa',
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
              '&.Mui-focused': {
                backgroundColor: '#fff',
              },
            }
          }}
        />
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Employees - Always show */}
        <Grid item xs={12} sm={6} md={4} lg={filters.status === 'all' ? 1.7 : 12}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <People sx={{ fontSize: 32, color: '#081122' }} />
              </Box>
              <Typography variant="h4" sx={{ color: '#081122', fontWeight: 600 }}>
                {stats.totalEmployees}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                Total Employees
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Present Today - Show when "all" or "present" is selected */}
        {(filters.status === 'all' || filters.status === 'present') && (
          <Grid item xs={12} sm={6} md={4} lg={1.7}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <CheckCircle sx={{ fontSize: 32, color: '#4caf50' }} />
              </Box>
              <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 600 }}>
                {stats.presentToday}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                Present Today
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        )}
        
        {/* Absent Today - Show when "all" or "absent" is selected */}
        {(filters.status === 'all' || filters.status === 'absent') && (
          <Grid item xs={12} sm={6} md={4} lg={1.7}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <Cancel sx={{ fontSize: 32, color: '#f44336' }} />
              </Box>
              <Typography variant="h4" sx={{ color: '#f44336', fontWeight: 600 }}>
                {stats.absentToday}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                Absent Today
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        )}
        
        {/* Late Today - Show when "all" or "late" is selected */}
        {(filters.status === 'all' || filters.status === 'late') && (
          <Grid item xs={12} sm={6} md={4} lg={1.7}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <Warning sx={{ fontSize: 32, color: '#ff9800' }} />
              </Box>
              <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 600 }}>
                {stats.lateToday}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                Late Today
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        )}
        
        {/* Week Off Today - Show when "all" or "week_off" is selected */}
        {(filters.status === 'all' || filters.status === 'week_off') && (
          <Grid item xs={12} sm={6} md={4} lg={1.7}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <Schedule sx={{ fontSize: 32, color: '#9c27b0' }} />
                </Box>
                <Typography variant="h4" sx={{ color: '#9c27b0', fontWeight: 600 }}>
                  {stats.weekOffToday}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666666' }}>
                  Week Off Today
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        
        {/* Checked Out - Show when "all" or "out" is selected */}
        {(filters.status === 'all' || filters.status === 'out') && (
          <Grid item xs={12} sm={6} md={4} lg={1.7}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <ExitToApp sx={{ fontSize: 32, color: '#00bcd4' }} />
                </Box>
                <Typography variant="h4" sx={{ color: '#00bcd4', fontWeight: 600 }}>
                  {stats.checkedOutToday}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666666' }}>
                  Checked Out
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        
        {/* Avg Hours - Show when "all" is selected */}
        {filters.status === 'all' && (
          <Grid item xs={12} sm={6} md={4} lg={1.7}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <AccessTime sx={{ fontSize: 32, color: '#2196f3' }} />
              </Box>
              <Typography variant="h4" sx={{ color: '#2196f3', fontWeight: 600 }}>
                {stats.avgHoursToday.toFixed(1)}h
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                Avg Hours
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        )}
      </Grid>

      {/* Main Content */}
      <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            borderBottom: '1px solid #e0e0e0',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
            },
            '& .Mui-selected': {
              color: '#081122',
            },
          }}
        >
          <Tab label="Today's Attendance" />
          <Tab label="Attendance Reports" />
          <Tab label="Leave Management" />
          <Tab label="Shift Management" />
          <Tab label="Analytics" />
        </Tabs>

        {/* Today's Attendance Tab */}
        <TabPanel value={tabValue} index={0}>
          {/* Filters */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterList sx={{ mr: 1, color: '#6c757d' }} />
              <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
                      Filters
                    </Typography>
              </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Store</InputLabel>
                  <Select
                    value={filters.store_id}
                    label="Store"
                    onChange={(e) => setFilters(prev => ({ ...prev, store_id: e.target.value }))}
                  >
                    <MenuItem value="all">All Stores</MenuItem>
                    {stores.map((store) => (
                      <MenuItem key={store.id} value={store.id}>
                        {store.name} ({store.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Cluster</InputLabel>
                  <Select
                    value={filters.cluster_id}
                    label="Cluster"
                    onChange={(e) => setFilters(prev => ({ ...prev, cluster_id: e.target.value }))}
                  >
                    <MenuItem value="all">All Clusters</MenuItem>
                    {clusters.map((cluster) => (
                      <MenuItem key={cluster.id} value={cluster.id}>
                        {cluster.name} ({cluster.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Shift</InputLabel>
                  <Select
                    value={filters.shift_id}
                    label="Shift"
                    onChange={(e) => setFilters(prev => ({ ...prev, shift_id: e.target.value }))}
                  >
                    <MenuItem value="all">All Shifts</MenuItem>
                    {shifts.map((shift) => (
                      <MenuItem key={shift.id} value={shift.id}>
                        {shift.name}
                        {shift.start_time && shift.end_time
                          ? ` (${shift.start_time} – ${shift.end_time})`
                          : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="present">Present</MenuItem>
                    <MenuItem value="out">Checked Out</MenuItem>
                    <MenuItem value="absent">Absent</MenuItem>
                    <MenuItem value="late">Late</MenuItem>
                    <MenuItem value="week_off">Week Off</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={filters.role}
                    label="Role"
                    onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
                  >
                    <MenuItem value="all">All Roles</MenuItem>
                    <MenuItem value="cluster_level_manager">Cluster Level Manager</MenuItem>
                    <MenuItem value="store_manager">Store Manager</MenuItem>
                    <MenuItem value="quality_manager">Quality Manager</MenuItem>
                    <MenuItem value="process_expert">Process Expert</MenuItem>
                    <MenuItem value="shift_in_charge">Shift In-Charge</MenuItem>
                    <MenuItem value="associate">Associate</MenuItem>
                    <MenuItem value="security_staff">Security Staff</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Late</InputLabel>
                  <Select
                    value={filters.is_late}
                    label="Late"
                    onChange={(e) => setFilters(prev => ({ ...prev, is_late: e.target.value }))}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="true">Late Only</MenuItem>
                    <MenuItem value="false">On Time Only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  size="small"
                  type="date"
                  label="Start Date"
                  value={filters.start_date}
                  onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  size="small"
                  type="date"
                  label="End Date"
                  value={filters.end_date}
                  onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    size="small"
                    type="number"
                    label="Min Hours"
                    value={filters.min_hours}
                    onChange={(e) => setFilters(prev => ({ ...prev, min_hours: e.target.value }))}
                  fullWidth
                  />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    size="small"
                    type="number"
                    label="Max Hours"
                    value={filters.max_hours}
                    onChange={(e) => setFilters(prev => ({ ...prev, max_hours: e.target.value }))}
                  fullWidth
                  />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                  <Button
                    variant="outlined"
                  startIcon={<Refresh />}
                    onClick={clearFilters}
                  fullWidth
                    size="small"
                  >
                  Clear Filters
                  </Button>
              </Grid>
            </Grid>
            </Paper>

            {/* Main Content Area */}
          <Box>

          <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Store</TableCell>
                    <TableCell>Cluster</TableCell>
                    <TableCell>Shift</TableCell>
                    <TableCell>Clock In</TableCell>
                    <TableCell>Clock Out</TableCell>
                    <TableCell>Total Hours</TableCell>
                    <TableCell>Breaks</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Punch In Photo</TableCell>
                    <TableCell>Punch Out Photo</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAttendanceData.map((attendance) => (
                    <TableRow 
                      key={attendance.id}
                      onClick={() => handleUserClick(attendance.user.id)}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#f5f5f5' }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <ProfileAvatar
                            imageUrl={attendance.user?.profile_image_url}
                            firstName={attendance.user?.first_name}
                            lastName={attendance.user?.last_name}
                            sx={{ width: 32, height: 32, bgcolor: '#081122' }}
                          />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {attendance.user?.first_name} {attendance.user?.last_name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666666' }}>
                              {attendance.user?.employee_id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#333333' }}>
                          {attendance.store?.name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#333333' }}>
                          {attendance.user?.cluster?.name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ color: '#333333', fontWeight: 500 }}>
                            {attendance.shift?.name || attendance.user?.shift?.name || 'N/A'}
                          </Typography>
                          {attendance.shift?.start_time && attendance.shift?.end_time && (
                            <Typography variant="caption" sx={{ color: '#666666' }}>
                              {attendance.shift.start_time} - {attendance.shift.end_time}
                            </Typography>
                          )}
                          {attendance.is_late && attendance.late_minutes && (
                            <Typography variant="caption" sx={{ color: '#ff9800', display: 'block', mt: 0.5 }}>
                              {attendance.late_minutes}m late
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatTime(attendance.clock_in_time)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatTime(attendance.clock_out_time)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatTotalHours(attendance)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: attendance.breaks && attendance.breaks.length > 0 ? '#333' : '#999' }}>
                          {formatBreaks(attendance)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                          <Chip
                            icon={getStatusIcon(attendance.status, attendance.is_late, attendance.is_half_day)}
                            label={getStatusLabel(attendance.status, attendance.is_late, attendance.late_minutes, attendance.is_half_day)}
                            color={getStatusColor(attendance.status, attendance.is_late, attendance.is_half_day) as any}
                            size="small"
                          />
                      </TableCell>
                      <TableCell>
                        {attendance.clock_in_photo_url ? (
                          <img
                            src={attendance.clock_in_photo_url}
                            alt="Punch In"
                            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedImageUrl(attendance.clock_in_photo_url);
                              setSelectedImageAlt('Punch In Photo');
                              setImageDialogOpen(true);
                            }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ color: '#999' }}>No photo</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {attendance.clock_out_photo_url ? (
                          <img
                            src={attendance.clock_out_photo_url}
                            alt="Punch Out"
                            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedImageUrl(attendance.clock_out_photo_url);
                              setSelectedImageAlt('Punch Out Photo');
                              setImageDialogOpen(true);
                            }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ color: '#999' }}>No photo</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedAttendance(attendance);
                            setDetailModalOpen(true);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Attendance Reports Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
              Attendance Reports
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={() => handleExportClick('inactive')}
                sx={{ borderColor: '#081122', color: '#081122' }}
              >
                Inactive Employees Report
              </Button>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => handleExportClick('active')}
                sx={{
                  background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
                  },
                  boxShadow: '0 4px 12px rgba(8, 17, 34, 0.3)',
                }}
              >
                Generate Report
              </Button>
            </Box>
          </Box>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            Generate detailed attendance reports for specific date ranges and stores. The report will include employee details, summary statistics, and daily attendance status.
            Use <strong>Inactive Employees Report</strong> for deactivated staff (leavers, notice period) who punched in during the selected dates, so their worked days can be paid.
          </Alert>
        </TabPanel>

        {/* Leave Management Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
              Leave Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{
                background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
                },
                boxShadow: '0 4px 12px rgba(8, 17, 34, 0.3)',
              }}
            >
              Add Leave Request
            </Button>
          </Box>
          
          <Alert severity="info">
            Leave management functionality will be implemented here. You can manage leave requests, approvals, and track leave balances.
          </Alert>
        </TabPanel>

        {/* Shift Management Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
              Shift Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{
                background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
                },
                boxShadow: '0 4px 12px rgba(8, 17, 34, 0.3)',
              }}
            >
              Create Shift
            </Button>
          </Box>
          
          <Alert severity="info">
            Shift management functionality will be implemented here. You can create shifts, assign employees, and manage shift schedules.
          </Alert>
        </TabPanel>

        {/* Analytics Tab */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
              Attendance Analytics
            </Typography>
            <Button
              variant="outlined"
              startIcon={<TrendingUp />}
              sx={{
                borderColor: '#6c757d',
                color: '#6c757d',
                '&:hover': {
                  borderColor: '#081122',
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              View Trends
            </Button>
          </Box>
          
          <Alert severity="info">
            Attendance analytics functionality will be implemented here. You can view trends, patterns, and insights about employee attendance.
          </Alert>
        </TabPanel>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Attendance Detail Modal */}
      <AttendanceDetailModal
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedAttendance(null);
        }}
        attendance={selectedAttendance}
      />

      {/* Image Dialog */}
      <ImageDialog
        open={imageDialogOpen}
        onClose={() => {
          setImageDialogOpen(false);
          setSelectedImageUrl(null);
          setSelectedImageAlt('');
        }}
        imageUrl={selectedImageUrl}
        alt={selectedImageAlt}
      />

      {/* Export Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => !exporting && setExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {exportFilters.employee_status === 'inactive' ? 'Export Inactive Employees Attendance' : 'Export Attendance Data'}
            </Typography>
            <IconButton
              onClick={() => !exporting && setExportDialogOpen(false)}
              disabled={exporting}
              size="small"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Store</InputLabel>
              <Select
                value={exportFilters.store_id}
                onChange={(e) => setExportFilters({ ...exportFilters, store_id: e.target.value })}
                label="Store"
                disabled={exporting}
              >
                <MenuItem value="all">All Stores</MenuItem>
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Start Date"
              type="date"
              value={exportFilters.start_date}
              onChange={(e) => setExportFilters({ ...exportFilters, start_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              disabled={exporting}
            />

            <TextField
              label="End Date"
              type="date"
              value={exportFilters.end_date}
              onChange={(e) => setExportFilters({ ...exportFilters, end_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              disabled={exporting}
            />

            <Alert severity="info">
              {exportFilters.employee_status === 'inactive'
                ? 'Includes deactivated employees who punched in at least once in the selected dates, with their last punch date. Days after they stopped coming show as Absent.'
                : 'The CSV file will include employee details, summary (Present, Absent, Week Off, etc.), and daily attendance status for the selected date range.'}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setExportDialogOpen(false)}
            disabled={exporting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting || !exportFilters.start_date || !exportFilters.end_date}
            variant="contained"
            startIcon={exporting ? <CircularProgress size={20} /> : <Download />}
            sx={{
              background: 'linear-gradient(135deg, #081122 0%, #1a2a3a 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a2a3a 0%, #081122 100%)',
              },
            }}
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttendancePage;
